import React, { useState } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  IconButton,
  Square,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { FiUploadCloud, FiTrash2 } from 'react-icons/fi/index.js';
import { Sheet } from 'lucide-react';
import { useCSVDownloader, useCSVReader, formatFileSize } from 'react-papaparse';

interface CsvData {
  headers: string[];
  rows: string[][];
  errors: string[];
}

interface ResultsInterface {
  data: [];
  errors: [];
  meta: [];
}

const requiredHeaders = ['SITE', 'REGION', 'STATE'];

export function CSVReader({
  onRead,
  onFileRemove,
}: {
  onRead: (results: CsvData) => void;
  onFileRemove: () => void;
}) {
  const { CSVReader } = useCSVReader();
  const [errors, setErrors] = useState<Array<string>>([]);

  const handleFileRemove = () => {
    setErrors([]);
    onFileRemove();
  };

  const handleCsvUpload = (results: ResultsInterface): CsvData => {
    const validationErrors = validateCsv(results.data, requiredHeaders);
    setErrors(validationErrors);

    const value: string[][] = results.data;
    const filtered = value.filter((_, i) => i !== 0);

    return {
      headers: value[0] ?? [],
      rows: filtered,
      errors: validationErrors,
    };
  };

  const validateCsv = (data: Array<string[]>, requiredHeaders: string[]) => {
    const errors: string[] = [];

    const headerRow = data[0];
    if (!headerRow) {
      errors.push('CSV data is empty');
      return errors;
    }

    // Validate header length
    if (headerRow.length !== requiredHeaders.length) {
      errors.push(
        `Incorrect number of headers (expected: ${requiredHeaders.length}, found: ${headerRow.length})`,
      );
    }

    // Validate header names
    const missingHeaders = requiredHeaders.filter((header) => !headerRow.includes(header));
    if (missingHeaders.length > 0) {
      errors.push(`Missing required headers: ${missingHeaders.join(', ')}`);
    }

    const invalidHeaders = headerRow.filter((header, index) => requiredHeaders[index] !== header);
    if (invalidHeaders.length > 0) {
      errors.push(`Invalid header names: ${invalidHeaders.join(', ')}`);
    }

    return errors;
  };

  return (
    <CSVReader
      onUploadAccepted={(results: ResultsInterface) => {
        onRead(handleCsvUpload(results));
      }}
      onRemoveFile={handleFileRemove}
      config={{
        worker: true,
      }}
      noDrag
    >
      {({ getRootProps, acceptedFile, getRemoveFileProps }) => (
        <>
          {errors.length > 0 && (
            <Box my={1}>
              <Alert
                status="error"
                flexDirection="column"
                alignItems="flex-start"
                borderRadius="lg"
              >
                <HStack mb={2}>
                  <AlertIcon />
                  <AlertTitle>CSV Import Errors!</AlertTitle>
                </HStack>
                <AlertDescription>
                  <Text fontSize="sm" mb={2}>
                    The following rows contain errors and need to be corrected:
                  </Text>
                  <Box as="ul" color="red" fontSize="sm" margin="10px 0">
                    {errors.map((error) => (
                      <Box as="li" key={error}>
                        {error}
                      </Box>
                    ))}
                  </Box>
                </AlertDescription>
              </Alert>
            </Box>
          )}
          <Center borderWidth="1px" borderRadius="lg" p="2">
            {acceptedFile ? (
              <Box alignContent={'start'} width={'full'}>
                <HStack spacing="3" bg="bg.subtle" justify={'space-between'}>
                  <Flex>
                    <Square size="10" bg="bg.surface" borderRadius="lg">
                      <Icon as={Sheet} boxSize="8" color="success" />
                    </Square>
                    <VStack align={'start'} spacing="0" ml={2}>
                      <Text textStyle="sm" fontWeight="bold">
                        {acceptedFile.name}
                      </Text>
                      <Text fontSize="xs" color="fg.muted">
                        {formatFileSize(acceptedFile.size)}
                      </Text>
                    </VStack>
                  </Flex>
                  <Flex {...getRemoveFileProps()}>
                    <IconButton
                      {...getRemoveFileProps()}
                      _hover={{ color: 'red' }}
                      icon={<FiTrash2 />}
                      variant="tertiary"
                      aria-label="Remove file"
                      onClick={handleFileRemove}
                    />
                  </Flex>
                </HStack>
              </Box>
            ) : (
              <VStack spacing="3" {...getRootProps()}>
                <Square size="10" bg="transparent" borderRadius="lg">
                  <Icon as={FiUploadCloud} boxSize="8" color="fg.muted" />
                </Square>
                <VStack spacing="1">
                  <HStack spacing="1" whiteSpace="nowrap">
                    <Button variant="text" colorScheme="blue" size="sm">
                      Click to upload
                    </Button>
                  </HStack>
                  <Text textStyle="xs" color="fg.muted">
                    CSV up to 2MB
                  </Text>
                </VStack>
              </VStack>
            )}
          </Center>
        </>
      )}
    </CSVReader>
  );
}

export function CSVLocationsDownloader({
  children,
  data,
  filename,
  type = 'link',
}: {
  children: React.ReactNode;
  data: any;
  filename: string;
  delimiter?: string;
  type?: 'link' | 'button';
  bom?: boolean;
}) {
  const { CSVDownloader, Type } = useCSVDownloader();

  return (
    <CSVDownloader type={Type[type]} bom={true} filename={filename} delimiter={','} data={data}>
      {children}
    </CSVDownloader>
  );
}
